import { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from 'src/lib/utils';
import { BetaChartCard } from 'src/pages/reports/components/beta-charts';
import { BetaChartAreaSkeleton } from 'src/pages/reports/components/beta-charts/beta-chart-skeletons';
import { BetaChartTooltip } from 'src/pages/reports/components/beta-charts/beta-chart-tooltip';
import { formatCompactNumber } from './overview-beta-format';

export interface ImpressionsSpendPoint {
  date: string;
  dateLabel: string;
  impressions: number;
  spend: number;
}

interface OverviewBetaImpressionsSpendProps {
  data: ImpressionsSpendPoint[];
  isLoading?: boolean;
}

type ChartType = 'area' | 'line' | 'bar';

export function OverviewBetaImpressionsSpend({
  data,
  isLoading = false,
}: OverviewBetaImpressionsSpendProps) {
  const [chartType, setChartType] = useState<ChartType>('area');

  const controls = (
    <div className="inline-flex rounded-lg bg-[#EDEEF2] p-0.5">
      {(['area', 'line', 'bar'] as ChartType[]).map((type) => (
        <button
          key={type}
          type="button"
          onClick={() => setChartType(type)}
          className={cn(
            'rounded-md px-3 py-1 text-[11.5px] font-semibold capitalize transition-all',
            chartType === type
              ? 'bg-white text-[#0D0F1A] shadow-[0_1px_2px_rgba(13,15,26,0.05)]'
              : 'bg-transparent text-[#5E6278]'
          )}
        >
          {type}
        </button>
      ))}
    </div>
  );

  const commonAxes = (
    <>
      <CartesianGrid
        stroke="#EDEEF2"
        vertical={false}
      />
      <XAxis
        dataKey="dateLabel"
        tick={{ fill: '#8A8EA0', fontSize: 11 }}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        yAxisId="left"
        tick={{ fill: '#8A8EA0', fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        width={48}
        tickFormatter={(value) => formatCompactNumber(Number(value))}
      />
      <YAxis
        yAxisId="right"
        orientation="right"
        tick={{ fill: '#8A8EA0', fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        width={48}
        tickFormatter={(value) => formatCompactNumber(Number(value), { currency: true })}
      />
      <Tooltip
        content={(
          <BetaChartTooltip
            valueFormatter={(value, name) =>
              name?.toLowerCase().includes('spend')
                ? formatCompactNumber(value, { currency: true })
                : formatCompactNumber(value)
            }
          />
        )}
      />
    </>
  );

  return (
    <BetaChartCard
      title="Impressions vs Spend"
      subtitle="Volume and cost correlation over time"
      controls={controls}
      className="mb-0"
    >
      {isLoading ? (
        <BetaChartAreaSkeleton className="h-[240px]" />
      ) : data.length === 0 ? (
        <div className="flex h-[240px] items-center justify-center text-sm text-[#8A8EA0]">
          No trend data for the selected period.
        </div>
      ) : (
        <div className="h-[240px] w-full">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            {chartType === 'bar' ? (
              <BarChart
                data={data}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                {commonAxes}
                <Bar
                  yAxisId="left"
                  dataKey="impressions"
                  name="Impressions"
                  fill="#059669"
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                />
                <Bar
                  yAxisId="right"
                  dataKey="spend"
                  name="Spend"
                  fill="#EF4444"
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            ) : chartType === 'line' ? (
              <LineChart
                data={data}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                {commonAxes}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="impressions"
                  name="Impressions"
                  stroke="#059669"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="spend"
                  name="Spend"
                  stroke="#EF4444"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            ) : (
              <AreaChart
                data={data}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="imprFill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="#059669"
                      stopOpacity={0.25}
                    />
                    <stop
                      offset="100%"
                      stopColor="#059669"
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                  <linearGradient
                    id="spendFill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="#EF4444"
                      stopOpacity={0.2}
                    />
                    <stop
                      offset="100%"
                      stopColor="#EF4444"
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>
                {commonAxes}
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="impressions"
                  name="Impressions"
                  stroke="#059669"
                  fill="url(#imprFill)"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="spend"
                  name="Spend"
                  stroke="#EF4444"
                  fill="url(#spendFill)"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </BetaChartCard>
  );
}
